import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { getCategories, generateCards, trackPageView } from "@/lib/api";

// Loading phrases for the spinner
const LOADING_PHRASES = [
  "Kontextus értelmezése...",
  "A beszélgetés keretének felépítése...",
  "A fókusz pontosítása...",
  "Mélységi ív kialakítása...",
  "A kérdések sorrendjének összehangolása...",
  "A hangvétel beállítása...",
];

export default function GeneratorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [loadingPhraseIndex, setLoadingPhraseIndex] = useState(0);
  const [minLoadingComplete, setMinLoadingComplete] = useState(false);
  const [apiDataReady, setApiDataReady] = useState(null);

  const [categories, setCategories] = useState([]);
  const [level1Options, setLevel1Options] = useState([]);
  const [level2Options, setLevel2Options] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedLevel1, setSelectedLevel1] = useState(null);
  const [selectedLevel2, setSelectedLevel2] = useState(null);

  const loadingIntervalRef = useRef(null);

  useEffect(() => {
    trackPageView("create");
    loadCategories();
  }, []);

  useEffect(() => {
    if (location.state?.returnToStep === 3) {
      try {
        const saved = JSON.parse(localStorage.getItem("generatorSelections"));
        if (saved?.selectedCategory && saved?.selectedLevel1 && saved?.selectedLevel2) {
          setSelectedCategory(saved.selectedCategory);
          setSelectedLevel1(saved.selectedLevel1);
          setSelectedLevel2(saved.selectedLevel2);
          setStep(3);
        }
      } catch (_) {}
    }
  }, [location.state]);

  useEffect(() => {
    if (generating) {
      setLoadingPhraseIndex(0);
      setMinLoadingComplete(false);

      let phraseCount = 0;
      loadingIntervalRef.current = setInterval(() => {
        phraseCount++;
        if (phraseCount < LOADING_PHRASES.length) {
          setLoadingPhraseIndex(phraseCount);
        } else {
          setMinLoadingComplete(true);
          clearInterval(loadingIntervalRef.current);
        }
      }, 4000);

      return () => {
        if (loadingIntervalRef.current)
          clearInterval(loadingIntervalRef.current);
      };
    }
  }, [generating]);

  useEffect(() => {
    if (minLoadingComplete && apiDataReady) {
      navigate(`/editor/${apiDataReady}`);
    }
  }, [minLoadingComplete, apiDataReady, navigate]);

  const loadCategories = async () => {
    try {
      const response = await getCategories();
      setCategories(response.data.categories);
      setLevel1Options(response.data.level1);
      setLevel2Options(response.data.level2);
    } catch (error) {
      toast.error("Nem sikerült betölteni a kategóriákat");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredItems = () => {
    switch (step) {
      case 1:
        return categories;
      case 2:
        return level1Options.filter(
          (l) => l.parent_id === selectedCategory?.id,
        );
      case 3:
        return level2Options.filter(
          (l) => l.parent_level1_id === selectedLevel1?.id,
        );
      default:
        return [];
    }
  };

  const handleSelect = (item) => {
    switch (step) {
      case 1:
        setSelectedCategory(item);
        setSelectedLevel1(null);
        setSelectedLevel2(null);
        break;
      case 2:
        setSelectedLevel1(item);
        setSelectedLevel2(null);
        break;
      case 3:
        setSelectedLevel2(item);
        break;
    }
  };

  const getCurrentSelection = () => {
    switch (step) {
      case 1:
        return selectedCategory;
      case 2:
        return selectedLevel1;
      case 3:
        return selectedLevel2;
      default:
        return null;
    }
  };

  const canProceed = () => getCurrentSelection() !== null;

  const handleNext = async () => {
    if (!canProceed()) return;
    if (step < 3) {
      setStep(step + 1);
    } else {
      await handleGenerate();
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
    else navigate("/");
  };

  const handleGenerate = async () => {
    if (!selectedCategory || !selectedLevel1 || !selectedLevel2) {
      toast.error("Kérlek, végezd el az összes választást");
      return;
    }

    localStorage.setItem(
      "generatorSelections",
      JSON.stringify({ selectedCategory, selectedLevel1, selectedLevel2 })
    );
    setGenerating(true);
    setApiDataReady(null);

    try {
      const response = await generateCards({
        category_id: selectedCategory.id,
        level1_id: selectedLevel1.id,
        level2_id: selectedLevel2.id,
      });
      setApiDataReady(response.data.project.id);
    } catch (error) {
      toast.error("Hiba történt a generálás során. Próbáld újra.");
      setGenerating(false);
    }
  };

  const items = getFilteredItems();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <style>{`
          @keyframes spinAnimation {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
        <div className="relative w-8 h-8">
          <div
            className="absolute inset-0 border-3 border-[#FF6C6C]/20 border-t-[#FF6C6C] rounded-full"
            style={{ animation: "spinAnimation 1s linear infinite" }}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Kártyakészítő | Kardiy</title>
        <meta name="description" content="Válaszd ki a helyzeted és a fókuszt – a Kardiy felépíti a személyre szabott kérdésívet." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
    <div className="h-screen bg-gradient-to-b from-[#FDFBF7] to-[#FFF1F2] flex flex-col overflow-hidden">
      {/* Full-screen generating overlay - SOLID background */}
      {generating && (
        <div className="fixed inset-0 z-50 bg-[#FDFBF7] flex flex-col items-center justify-center">
          <style>{`
            @keyframes spinAnimation {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
          `}</style>
          <div className="relative w-16 h-16">
            <div
              className="absolute inset-0 border-4 border-[#FF6C6C]/20 border-t-[#FF6C6C] rounded-full"
              style={{ animation: "spinAnimation 1s linear infinite" }}
            />
            <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-[#FF6C6C]" />
          </div>
          <h2 className="font-heading text-lg font-bold text-[#292524] mt-5 text-center px-6">
            {LOADING_PHRASES[loadingPhraseIndex]}
          </h2>
          <div className="flex gap-2 mt-10 md:mt-6">
            {LOADING_PHRASES.map((_, i) => (
              <div
                key={i}
                className={`w-8 h-1 rounded-full transition-colors ${i <= loadingPhraseIndex ? "bg-[#FF6C6C]" : "bg-stone-200"}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="flex-shrink-0 bg-[#FDFBF7] border-b border-stone-100">
        <div className="max-w-md mx-auto px-4 flex items-center justify-between h-12">
          <button onClick={handleBack} className="p-2 -ml-2 text-stone-500">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#FF6C6C]" />
            <span className="font-heading text-base font-bold text-[#292524]">
              Kardiy
            </span>
          </div>
          <div className="w-9" />
        </div>
      </header>

      {/* Progress bar */}
      <div className="flex-shrink-0 px-5 pt-2 pb-1">
        <div className="max-w-md mx-auto flex gap-1.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <div
              key={s}
              className={`flex-1 h-1 rounded-full ${s <= step ? "bg-[#FF6C6C]" : "bg-stone-200"}`}
            />
          ))}
        </div>
      </div>

      {/* Breadcrumb path - always reserve space for consistency */}
      <div className="flex-shrink-0 px-4 h-8 pt-10">
        <div className="max-w-md mx-auto flex flex-wrap items-center justify-center gap-2">
          {selectedCategory && (
            <span className="px-2 py-1.5 bg-[#FF52FF]/10 text-[#292524] rounded-full text-[15px] font-medium">
              {selectedCategory.name}
            </span>
          )}
          {selectedLevel1 && (
            <>
              <span className="text-stone-300 text-[15px]">›</span>
              <span className="px-2 py-0.5 bg-[#FF5252]/20 text-[#292524] rounded-full text-[15px] font-medium">
                {selectedLevel1.name}
              </span>
            </>
          )}
          {selectedLevel2 && (
            <>
              <span className="text-stone-300 text-[10px]">›</span>
              <span className="px-2 py-0.5 bg-[#52FF52]/20 text-[#292524] rounded-full text-[15px] font-medium">
                {selectedLevel2.name}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Main Content - bigger cards, less gap */}
      <main className="flex-1 px-10 pt-20 pb-24 flex flex-col justify-start max-w-md mx-auto w-full">
        {/* Options Grid - bigger cards */}
        <div
          className={`grid gap-3 ${items.length <= 4 ? "grid-cols-2" : items.length <= 6 ? "grid-cols-2" : "grid-cols-3"}`}
        >
          {items.map((item) => {
            const isSelected = getCurrentSelection()?.id === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleSelect(item)}
                className={`p-4 rounded-xl transition-all active:scale-95 ${
                  isSelected
                    ? "bg-[#FF6B6B] shadow-md"
                    : "bg-white shadow-sm hover:shadow-md"
                }`}
                data-testid={`option-${item.id}`}
              >
                <span
                  className={`font-medium text-base ${isSelected ? "text-white" : "text-[#292524]"}`}
                >
                  {item.name}
                </span>
              </button>
            );
          })}
        </div>
      </main>

      {/* Bottom Action */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-100 px-3 pt-3"
        style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}
      >
        <div className="max-w-md mx-auto">
          <Button
            onClick={handleNext}
            disabled={!canProceed() || generating}
            className="w-full bg-[#FF6B6B] hover:bg-[#FF5252] disabled:bg-stone-200 text-white font-bold py-5 rounded-full text-base"
          >
            {step === 3 ? (
              <>
                Kártyák generálása <Sparkles className="ml-2 w-4 h-4" />
              </>
            ) : (
              <>Tovább </>
            )}
          </Button>
        </div>
      </div>
    </div>
    </>
  );
}
